import { Checkbox, Grid } from '@mui/material';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import moment from 'moment';
import GeoFences from '../../../assets/svg/onboarding/icons/GeoFences';
import TimeWindows from '../../../assets/svg/onboarding/icons/TimeWindows';
import Weight from '../../../assets/svg/onboarding/icons/Weight';
import { Project } from '../../../domain/project/Project';
import Text from '../../../react-components/Text';
import eventsPanel from '../../../services/events';
import { useUpdateProjectOnboarding } from '../hooks/useUpdateProjectOnboarding';
import { OnboardingNextStepButton } from '../OnboardingNextStepButton';

const availableConstraints = [`capacity`, `time_windows`, `geo_fences`];

interface ConstraintsProps {
  project: Project;
  onFinish: () => void;
}

export const Constraints = ({ project, onFinish }: ConstraintsProps) => {
  const { success, run: updateProject } = useUpdateProjectOnboarding();
  const [constraints, setConstraints] = useState<string[]>([]);

  useEffect(() => {
    const originalConstraints = [];
    if (
      project.constraints.volume &&
      project.constraints.volume &&
      project.constraints.weight &&
      project.constraints.weight
    ) {
      originalConstraints.push(`capacity`);
    }

    if (project.constraints.time_windows) {
      originalConstraints.push(`time_windows`);
    }

    if (project.geo_fences_enabled) {
      originalConstraints.push(`geo_fences`);
    }
    setConstraints(originalConstraints);
  }, [project]);

  useEffect(() => {
    if (success) {
      eventsPanel.onboardingPageView({ page: `constraints`, data: { constraints } });
      onFinish();
    }
  });

  const isConstraintActive = (c: string) => constraints.includes(c);

  const getIconByConstraint = (c: string) => {
    switch (c) {
      case `capacity`:
        return <Weight style={{ width: `20px` }} color={RoutalPalette.primary40} />;
      case `time_windows`:
        return <TimeWindows style={{ width: `20px` }} color={RoutalPalette.primary40} />;
      case `geo_fences`:
        return <GeoFences style={{ width: `20px` }} color={RoutalPalette.primary40} />;
      default:
        return null;
    }
  };

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{ padding: `20px`, maxWidth: `660px`, position: `relative` }}
    >
      <Grid item>
        <Text variant="h6" intlId="company.onboarding.constraints.info" />
      </Grid>

      <VSpacer medium />

      <Grid item container direction="column" wrap="nowrap">
        {availableConstraints.map((c, index) => (
          <Grid
            key={c}
            item
            container
            direction="row"
            wrap="nowrap"
            alignItems="center"
            justifyContent="center"
            style={{
              cursor: `pointer`,
              borderRadius: `10px`,
              padding: `20px`,
              height: `76px`,
              border: isConstraintActive(c)
                ? `2px solid ${RoutalPalette.primary40}`
                : `2px solid ${RoutalPalette.neutral10}`,
              marginBottom: index < availableConstraints.length - 1 ? `12px` : `0px`,
            }}
            onClick={() => {
              if (isConstraintActive(c)) {
                setConstraints(constraints.filter((constraint) => constraint !== c));
              } else {
                setConstraints([...constraints, c]);
              }
            }}
          >
            <Grid item style={{ width: `unset`, marginRight: `20px` }}>
              {getIconByConstraint(c)}
            </Grid>

            <Grid item container direction="column" wrap="nowrap">
              <Grid item>
                <Text
                  intlId={`company.onboarding.constraints.option.${c}`}
                  style={{
                    cursor: `pointer`,
                    color: RoutalPalette.primary40,
                  }}
                />
              </Grid>

              <Grid item>
                <VSpacer small />
              </Grid>
              <Grid item>
                <Text
                  variant="label"
                  intlId={`company.onboarding.constraints.option.${c}.info`}
                  style={{ cursor: `pointer`, marginRight: `20px` }}
                />
              </Grid>
            </Grid>

            <Grid item>
              <Checkbox checked={isConstraintActive(c)} size="medium" />
            </Grid>
          </Grid>
        ))}
      </Grid>

      <Grid
        item
        container
        direction="row"
        wrap="nowrap"
        alignItems="center"
        justifyContent="center"
        style={{ position: `absolute`, bottom: `-60px` }}
      >
        <OnboardingNextStepButton
          style={{
            width: `250px`,
            height: `35px`,
            padding: 0,
          }}
          onClick={() => {
            project.constraints.weight = constraints.includes(`capacity`);
            project.constraints.volume = constraints.includes(`capacity`);
            project.constraints.time_windows = constraints.includes(`time_windows`);

            updateProject({
              constraints: project.constraints,
              geo_fences_enabled: constraints.includes(`geo_fences`),
              timeZone: moment.tz.guess(true), // Leverage the query to add the browser timezone to the project
            });
          }}
        />
      </Grid>
    </Grid>
  );
};
